import { Texture } from 'pixi.js';
import { P, Raster } from './atelier';
import { ageVisual, visualAge } from '@data/visual-ages';
export type ActorKind = 'grunt' | 'defender' | 'brute' | 'boss' | 'worker' | 'citizen';
export type ActorAction = 'move' | 'attack';
export function actorAttack(kind: ActorKind, age: number) {
  if (visualAge(age) === 3 && kind !== 'boss' && kind !== 'brute') return 'rifle';
  return ageVisual(age).attack;
}
const cache = new Map<string, Texture>();
/** Four authored poses: gait, wheels, exhaust and weapon recoil use the same clock. */
export function actorArt(kind:ActorKind, age:number, frame=0, action:ActorAction='move'):Texture {
  age=visualAge(age);frame=((frame%4)+4)%4;
  const key=`actor:${kind}:${age}:${frame}:${action}`;const old=cache.get(key);if(old)return old;
  const r=new Raster(28,28), enemy=kind!=='worker'&&kind!=='citizen'&&kind!=='defender';
  const color=enemy?P.red:P.teal, bright=enemy?'#ed8574':P.cyan;
  const step=[0,1,0,-1][frame];
  if(age>=7&&(kind==='worker'||kind==='brute'||kind==='boss')) {
    advancedVehicle(r,kind,age,frame,action,color,bright);
    const t=r.texture();cache.set(key,t);return t;
  }
  const person=(x:number,y:number,weapon=true)=>{
    r.rect(x-2,y-12,4,3,age>=5?P.steel:P.tan);
    r.rect(x-1,y-10,3,3,age>=5?P.blue:'#e0ac7c');
    if(age===3)r.rect(x-1,y-14,3,3,P.cream);
    else if(age>=4)r.rect(x-2,y-13,5,2,age>=5?P.steel:P.forest);
    else r.poly([[x-2,y-12],[x,y-15],[x+2,y-12]],P.iron);
    r.rect(x-2,y-7,5,5,kind==='worker'?(age>=5?P.steel:P.shade):color);
    r.rect(x-3,y-6+step,1,4,P.tan);r.rect(x+3,y-6-step,1,3,P.tan);
    r.rect(x-2-step,y-2,2,3,P.iron);r.rect(x+1+step,y-2,2,3,P.ink);
    if(weapon){
      if(age===0){r.rect(x+5,y-14,1,15,P.timber);r.poly([[x+4,y-14],[x+5,y-17],[x+7,y-14]],P.steel);r.ellipse(x-3,y-5,2,3,color);}
      else if(age<=2){r.line(x+5,y-11,x+7,y-7,P.timber);r.line(x+7,y-7,x+5,y-3,P.timber);r.line(x+5,y-11,x+5,y-3,P.tan);}
      else{r.rect(x+2,y-7,7,2,age>=5?P.steel:P.iron);r.rect(x+8,y-7,2,1,age>=5?bright:P.wood);if(frame===1&&action==='attack')r.rect(x+10,y-8,2,2,age>=5?bright:P.gold);}
    }
  };
  if(age>=5&&(kind==='brute'||kind==='boss'||kind==='worker')) {
    const big=kind==='boss',w=big?12:9;
    if((age<=6&&kind!=='boss'&&kind!=='worker')||(age===5&&kind==='worker')) {
      r.rect(14-w,20,w*2,3,P.iron);for(let i=0;i<3;i++)r.ellipse(8+i*6,23,2,2,P.ink);
      r.poly([[14-w,19],[10,13],[21,13],[14+w,19]],P.steel);r.rect(12,14,7,3,color);if(kind!=='worker')r.rect(19,15,6,2,P.iron);else{r.rect(17,11,2,5,P.steel);r.rect(16,10,4,2,P.cyan);}
      r.rect(7+frame*3,23,1,1,P.steel);
    }else{
      r.poly([[2,18],[8,11],[14,7],[20,11],[26,18],[17,21],[11,21]],P.iron);
      r.poly([[4,17],[14,9],[24,17],[14,19]],age>=9?P.violet:P.steel);
      r.rect(11,12,6,3,color);r.rect(5,18,4,2,bright);r.rect(20,18,4,2,bright);
      r.rect(9,21,3,1+frame,bright);r.rect(17,21,3,4-frame,bright);
      if(big){r.rect(13,4,2,7,color);r.line(4,15,24,15,bright);}
      if(age>=10){r.ellipse(14,14,12,2,P.violet);r.line(4,14,24,14,bright);}
    }
  } else if(age>=4&&(kind==='brute'||kind==='boss'||kind==='worker')) {
    r.rect(3,19,23,5,P.iron);r.rect(5,20,19,2,P.ink);
    for(let i=0;i<4;i++)r.rect(5+i*5,21,2,2,(i+frame)%2?P.steel:P.rock);
    r.poly([[4,18],[8,13],[21,13],[25,18]],kind==='worker'?P.steel:color);
    if(kind==='worker'){r.rect(6,11,8,7,P.steel);r.rect(7,12,5,3,P.blue);r.rect(16,14,7,4,P.timber);}
    else {r.rect(10,10,10,5,P.rock);r.rect(17-(frame===1?1:0),11,10,2,P.iron);r.rect(11,10,6,1,color);}
  } else if(age===3&&(kind==='brute'||kind==='boss')) {
    r.rect(5,17,18,3,P.wood);r.ellipse(8,21,4,4,P.dark);r.ellipse(21,21,4,4,P.dark);
    r.line(8-3,21-step,8+3,21+step,P.timber);r.line(21-step,18,21+step,24,P.timber);
    r.poly([[7,16],[10,12],[25,11],[25,15],[11,18]],P.iron);r.rect(11,13,13,1,P.lightWood);
    if(frame===1&&action==='attack'){r.rect(25,10,3,5,P.gold);}
  } else if(kind==='worker'&&(age===2||age===3)) {
    // Two-wheel freight cart with a draught animal, not a mounted soldier.
    r.rect(2,15,13,6,P.wood);r.rect(3,13,11,3,P.tan);
    r.ellipse(5,23,3,3,P.dark);r.ellipse(13,23,3,3,P.dark);
    r.line(3,23-step,7,23+step,P.lightWood);r.rect(15,19,8,1,P.wood);
    r.rect(19,15,7,5,P.timber);r.rect(24,12,2,4,P.tan);
    r.rect(20-step,20,1,4,P.dark);r.rect(24+step,20,1,4,P.dark);
  } else if((age<=2&&(kind==='brute'||kind==='boss'))||(kind==='worker'&&age>0)) {
    // Mounted khans/armored cavalry and animal-drawn transport.
    r.rect(5,17,16,6,P.wood);r.rect(20,12,3,8,P.timber);r.rect(20,11,6,3,P.timber);
    r.rect(22,10,1,2,P.dark);r.rect(24,12,1,1,P.ink);
    r.line(5,18,2,21,P.dark);r.rect(7-step,22,2,4,P.dark);r.rect(18+step,22,2,4,P.dark);
    if(kind==='worker'){r.rect(6,13,10,5,P.shade);r.rect(8,12,6,2,P.tan);}
    else {person(12,16);if(kind==='boss'){r.rect(6,3,1,13,P.dark);r.rect(2,3,4,6,color);r.rect(3,4,1,3,P.gold);}}
  } else {person(13,25,kind!=='worker'&&kind!=='citizen');if(kind==='worker'){r.rect(7,17,4,5,P.timber);}}
  if(kind==='citizen'&&action==='attack'){r.rect(17,13+step*2,1,8,P.timber);r.rect(15,12+step*2,5,2,P.steel);}
  // Later space infantry evolves from suits into powered armor and energy bodies.
  if(age>=6&&(kind==='grunt'||kind==='defender')) {
    r.rect(8,16,2,6,P.steel);r.rect(16,16,2,6,P.steel);
    r.rect(10,13,6,1,bright);
    if(age>=8){r.rect(8,22,2,2+frame%2,bright);r.rect(16,22,2,3-frame%2,bright);}
    if(age>=10){r.ellipse(13,17,7,8,P.blue);r.poly([[13,9],[18,17],[13,24],[8,17]],bright);}
  }
  const t=r.texture();cache.set(key,t);return t;
}

function advancedVehicle(r:Raster,kind:ActorKind,age:number,frame:number,action:ActorAction,color:string,bright:string) {
  const boss=kind==='boss',worker=kind==='worker';
  if(age===7){
    r.rect(11,10,6,12,P.iron);r.rect(12,10,3,11,P.steel);
    for(const x of [2,19]){r.poly([[x,9],[x+6,6],[x+7,18],[x+1,21]],P.lightWood);for(let i=0;i<3;i++)r.line(x+1,10+i*3,x+5,8+i*3,P.gold);}
    r.rect(12,8,3,3,color);r.rect(12,22,3,1+frame,bright);
    if(boss){r.ellipse(14,15,12,3,P.gold);r.line(3,15,25,15,P.cream);}
  }else if(age===8){
    r.poly([[2,17],[6,12],[21,9],[27,14],[20,20],[7,21]],P.iron);
    r.poly([[4,16],[8,12],[21,10],[24,14],[17,17]],P.steel);
    r.rect(9,12,9,2,color);for(let i=0;i<4;i++)r.rect(8+i*3,18,2,1,bright);
    r.rect(3,18,3,1+frame,bright);r.rect(7,21,3,3-frame%2,bright);
    if(boss){r.rect(15,5,4,7,P.steel);r.rect(17,4,1,2,color);}
  }else if(age===9){
    for(const [x,y] of [[5,15],[14,9],[23,16]]){r.poly([[x,y-5],[x+4,y],[x,y+6],[x-4,y]],P.violet);r.line(x,y-4,x-3,y,bright);r.rect(x,y,1,3,P.iron);}
    r.line(7,18,14,22,P.blue);r.line(14,22,22,19,P.blue);
    if(boss){r.ellipse(14,15,7,10,P.iron);r.ellipse(14,15,4,7,color);}
    r.rect(13,22+frame%2,2,2,bright);
  }else if(age===10){
    r.poly([[1,9],[6,15],[14,18],[22,15],[27,9],[23,21],[14,25],[5,21]],P.steel);
    r.poly([[1,9],[14,17],[27,9],[22,18],[14,22],[6,18]],P.cream);
    r.line(5,20,14,24,P.gold);r.line(14,24,23,20,P.gold);
    r.poly([[14,6],[18,15],[14,21],[10,15]],color);r.rect(13,11,2,5,bright);
    if(boss)r.line(14,2,14,13,P.gold);
  }else{
    r.ellipse(14,15,12,9,P.gold);r.ellipse(14,15,9,6,P.iron);
    r.ellipse(14,15,5,12,P.pale);r.ellipse(14,15,3,9,P.blue);
    r.rect(13,11,2,7,bright);r.rect(3+frame*2,17,2,1,P.cream);
    if(boss){r.line(1,15,27,15,P.gold);r.rect(13,2,2,2,P.cream);}
  }
  if(worker){r.rect(11,16,5,3,P.teal);r.rect(12,17,3,1,P.cyan);}
  else if(action==='attack'&&frame===1){r.rect(24,13,3,2,bright);}
}
